import { createTheme } from '@mui/material/styles';

const getCssVars = (mode) => {
  const isDark = mode === 'dark';
  return {
    '--app-bg': isDark
      ? 'radial-gradient(circle at top, #0b3b2e 0%, #031a14 62%)'
      : 'radial-gradient(circle at top, #d5f8e8 0%, #f3fbf7 64%)',
    '--surface-1': isDark
      ? 'linear-gradient(180deg, rgba(255,255,255,0.1), rgba(255,255,255,0.03))'
      : 'linear-gradient(180deg, rgba(255,255,255,0.93), rgba(240,252,246,0.88))',
    '--surface-2': isDark
      ? 'linear-gradient(135deg, rgba(34,197,94,0.24), rgba(16,185,129,0.14))'
      : 'linear-gradient(135deg, rgba(22,163,74,0.2), rgba(16,185,129,0.13))',
    '--surface-3': isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
    '--border-subtle': isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.12)',
    '--text-primary': isDark ? '#ffffff' : '#0f172a',
    '--text-secondary': isDark ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.75)',
    '--text-muted': isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)',
    '--text-dim': isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    '--table-header-bg': isDark ? 'rgba(15,23,42,0.65)' : 'rgba(15,23,42,0.06)',
    '--row-hover-bg': isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
    '--input-bg': '#ffffff',
    '--navbar-bg': isDark
      ? 'linear-gradient(180deg, rgba(3,26,20,0.93) 0%, rgba(8,48,38,0.9) 100%)'
      : 'linear-gradient(180deg, rgba(243,251,247,0.95) 0%, rgba(232,247,239,0.9) 100%)',
    '--drawer-bg': isDark
      ? 'linear-gradient(180deg, rgba(3,26,20,0.98) 0%, rgba(8,48,38,0.98) 100%)'
      : 'linear-gradient(180deg, rgba(243,251,247,0.98) 0%, rgba(232,247,239,0.98) 100%)',
    '--icon-button-bg': isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)',
    '--icon-button-border': isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
    '--icon-button-hover-bg': isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,42,0.12)',
    '--glow-a': isDark
      ? 'radial-gradient(circle, rgba(34,197,94,0.38), rgba(2,6,23,0))'
      : 'radial-gradient(circle, rgba(22,163,74,0.2), rgba(248,250,252,0))',
    '--glow-b': isDark
      ? 'radial-gradient(circle, rgba(16,185,129,0.36), rgba(2,6,23,0))'
      : 'radial-gradient(circle, rgba(5,150,105,0.18), rgba(248,250,252,0))',
  };
};

export const createAppTheme = (mode = 'dark') =>
  createTheme({
    typography: {
      fontFamily: '"Manrope", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      button: {
        textTransform: 'none',
        fontWeight: 700,
      },
    },
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#34d399' : '#16a34a',
      },
      background: {
        default: mode === 'dark' ? '#031a14' : '#f3fbf7',
        paper: mode === 'dark' ? '#0b2e24' : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#0f172a',
        secondary: mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.75)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: mode,
          },
          body: {
            minHeight: '100vh',
            background: 'var(--app-bg)',
            color: 'var(--text-primary)',
            transition: 'background 200ms ease, color 200ms ease',
            ...getCssVars(mode),
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'dark'
              ? '0 12px 30px rgba(0,0,0,0.28)'
              : '0 12px 28px rgba(16,185,129,0.08)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            minHeight: 40,
          },
          containedPrimary: {
            background: 'linear-gradient(90deg, #16a34a, #059669)',
            color: '#ffffff',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
          },
        },
      },
    },
  });

